<template>
  <div>
    <p class="text-muted">
      The question catalogue. Packages pick questions from here; a question's scope decides whether
      it is asked once per account (inside a section) or once per person of a given type.
    </p>

    <b-row class="mb-1">
      <b-col cols="12" md="3" class="mb-50 mb-md-0">
        <b-form-input v-model="search" placeholder="Search label or key..." />
      </b-col>
      <b-col cols="12" md="3" class="mb-50 mb-md-0">
        <b-form-select v-model="scopeFilter" :options="scopeFilterOptions" />
      </b-col>
      <b-col cols="12" md="3" class="mb-50 mb-md-0">
        <b-form-select v-model="sectionFilter" :options="sectionFilterOptions" />
      </b-col>
      <b-col cols="12" md="3" class="d-flex justify-content-md-end">
        <b-button variant="success" @click="openCreate">
          New question
        </b-button>
      </b-col>
    </b-row>

    <b-table
      striped
      hover
      responsive
      class="builder-table"
      :items="filteredQuestions"
      :fields="fields"
      :current-page="currentPage"
      :per-page="perPage"
      show-empty
      empty-text="No questions match."
    >
      <template #cell(Label)="{ item }">
        <strong>{{ item.Label }}</strong>
        <small class="text-muted d-block">
          <code>{{ item.Key }}</code>
          <span v-if="item.BuiltinKey"> · built-in: {{ item.BuiltinKey }}</span>
        </small>
      </template>

      <template #cell(Scope)="{ value }">
        <b-badge :variant="value === 'Account' ? 'light-secondary' : 'light-primary'">
          {{ scopeLabel(data.scopes, value) }}
        </b-badge>
      </template>

      <template #cell(InputType)="{ item }">
        <span>{{ item.InputType }}</span>
        <small v-if="hasOptions(item.InputType)" class="text-muted d-block">
          {{ plural((item.Options || []).length, 'option') }}
        </small>
        <small v-if="item.Condition && item.Condition.when" class="text-muted d-block">
          <feather-icon icon="GitBranchIcon" size="12" /> conditional
        </small>
      </template>

      <template #cell(DefaultSectionKey)="{ item }">
        <span v-if="item.Scope !== 'Account'" class="text-muted">person section</span>
        <span v-else-if="item.DefaultSectionKey">
          <code>{{ item.DefaultSectionKey }}</code>
          <small class="text-muted">{{ item.SectionTitle || sectionTitle(data.sections, item.DefaultSectionKey) }}</small>
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(DefaultRequired)="{ value }">
        <feather-icon v-if="value" icon="CheckIcon" class="text-success" />
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(UsageCount)="{ item }">
        <span v-if="item.UsageCount">Used in {{ plural(item.UsageCount, 'item') }}</span>
        <span v-else class="text-muted">Not used</span>
        <b-badge v-if="item.IsSystem" variant="light-secondary" class="ml-25">System</b-badge>
      </template>

      <template #cell(actions)="{ item }">
        <b-button size="sm" variant="flat-primary" class="btn-icon" title="Edit" @click="openEdit(item)">
          <feather-icon icon="EditIcon" />
        </b-button>
        <b-button
          size="sm"
          variant="flat-danger"
          class="btn-icon"
          title="Delete"
          :disabled="!!item.IsSystem"
          @click="confirmDelete(item)"
        >
          <feather-icon icon="TrashIcon" />
        </b-button>
      </template>
    </b-table>

    <b-pagination v-model="currentPage" :total-rows="filteredQuestions.length" :per-page="perPage" />

    <question-modal ref="questionModal" :data="data" @saved="$emit('refresh')" />
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BTable, BPagination, BBadge, BFormInput, BFormSelect,
} from 'bootstrap-vue'
import store from '@/store'
import QuestionModal from './QuestionModal.vue'
import {
  apiError, plural, hasOptions, scopeLabel, sectionTitle,
} from './builderHelpers'

export default {
  name: 'QuestionsTab',

  components: {
    BRow, BCol, BButton, BTable, BPagination, BBadge, BFormInput, BFormSelect, QuestionModal,
  },

  props: {
    data: { type: Object, required: true },
  },

  data() {
    return {
      search: '',
      scopeFilter: null,
      sectionFilter: null,
      currentPage: 1,
      perPage: 15,
      fields: [
        { key: 'Label', label: 'Label', sortable: true },
        { key: 'Scope', label: 'Scope', sortable: true },
        { key: 'InputType', label: 'Input type', sortable: true },
        { key: 'DefaultSectionKey', label: 'Default section', sortable: true },
        { key: 'DefaultRequired', label: 'Required', class: 'text-center' },
        { key: 'UsageCount', label: 'Usage', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },

  computed: {
    scopeFilterOptions() {
      return [{ value: null, text: 'All scopes' }]
        .concat((this.data.scopes || []).map(s => ({ value: s.value, text: s.label })))
    },
    sectionFilterOptions() {
      return [{ value: null, text: 'All sections' }]
        .concat((this.data.sections || []).filter(s => !s.PersonType).map(s => ({ value: s.Key, text: `${s.Key} · ${s.Title}` })))
    },
    filteredQuestions() {
      const term = this.search.trim().toLowerCase()
      return (this.data.questions || []).filter(q => {
        if (this.scopeFilter && q.Scope !== this.scopeFilter) return false
        if (this.sectionFilter && q.DefaultSectionKey !== this.sectionFilter) return false
        if (!term) return true
        return (q.Label || '').toLowerCase().indexOf(term) !== -1
          || (q.Key || '').toLowerCase().indexOf(term) !== -1
      })
    },
  },

  watch: {
    search() { this.currentPage = 1 },
    scopeFilter() { this.currentPage = 1 },
    sectionFilter() { this.currentPage = 1 },
  },

  methods: {
    plural,
    hasOptions,
    scopeLabel,
    sectionTitle,

    openCreate() {
      const presets = { scope: this.scopeFilter || 'Account', defaultSectionKey: this.sectionFilter || null }
      this.$refs.questionModal.open(null, presets)
    },

    openEdit(question) {
      this.$refs.questionModal.open(question)
    },

    confirmDelete(question) {
      const usage = question.UsageCount
        ? ` It is used in ${plural(question.UsageCount, 'package item')}; the server will refuse while it is in use.`
        : ''
      this.$bvModal.msgBoxConfirm(`Delete question "${question.Label}"?${usage}`, {
        title: 'Delete question',
        okVariant: 'danger',
        okTitle: 'Delete',
        centered: true,
      }).then(ok => {
        if (!ok) return
        store.dispatch('settings/deleteOnboardingQuestion', { id: question.id })
          .then(res => {
            this.$bvToast.toast(res.data.message || 'Question deleted.', { variant: 'success', title: 'Deleted', solid: true })
            this.$emit('refresh')
          })
          .catch(err => {
            this.$bvToast.toast(apiError(err), { variant: 'danger', title: 'Error', solid: true })
          })
      })
    },
  },
}
</script>
